import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authApi } from 'auth/auth.api';
import { clearOtpSession, saveOtpSession } from 'auth/otpSession';
import { ApiError } from 'core/errors/ApiError';
import { firstFieldError, humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { Button } from 'shared/components/Button';
import { PageMeta } from 'shared/components/PageMeta';
import { TextInput } from 'shared/components/Input';
import { useCountdown } from 'shared/hooks/useCountdown';
import { useSubmitLock } from 'shared/hooks/useSubmitLock';
import { displayPhone, isLikelySaudiMobile, normalizeSaudiPhone } from 'shared/utilities/phone';
import { normalizeIndicDigits } from 'shared/utilities/digits';

export function PhoneAuthPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { pending, tryAcquire, release } = useSubmitLock();
  const [phone, setPhone] = useState('');
  const [channel, setChannel] = useState('sms');
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [retryAfter, setRetryAfter] = useState({ seconds: 0, startedAt: Date.now() });
  const retryIn = useCountdown(retryAfter.seconds, retryAfter.startedAt);
  const throttled = retryIn > 0;

  async function handleSubmit(event) {
    event.preventDefault();
    if (throttled || !tryAcquire()) {
      return;
    }
    const trimmed = displayPhone(phone).trim();
    if (!isLikelySaudiMobile(trimmed) && !isLikelySaudiMobile(phone)) {
      setFieldError(t('auth.phoneHint'));
      release();
      return;
    }
    setError('');
    setFieldError('');
    try {
      const data = await authApi.requestOtp({
        phone: normalizeSaudiPhone(trimmed || phone),
        channel,
      });
      clearOtpSession();
      saveOtpSession({ ...data, started_at: Date.now(), pending_code: '' });
      const returnToQuery = location.search || '';
      navigate(`${paths.verify}${returnToQuery}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setFieldError(firstFieldError(err.errors, 'phone') || '');
        if (err.isThrottled()) {
          if (err.retryAfterSeconds) {
            setRetryAfter({ seconds: err.retryAfterSeconds, startedAt: Date.now() });
          }
          setError(humanizeApiError(err, t));
        } else if (err.status === 503) {
          setError(t('auth.otpDeliveryUnavailable'));
        } else {
          setError(humanizeApiError(err, t));
        }
      } else {
        setError(t('errors.unexpected'));
      }
      release();
    }
  }

  return (
    <div className="auth-page">
      <PageMeta titleKey="meta.auth" />
      <form className="card auth-card" onSubmit={handleSubmit}>
        <h1>{t('auth.title')}</h1>
        <p className="muted">{t('auth.subtitle')}</p>
        <TextInput
          id="phone"
          label={t('auth.phone')}
          hint={t('auth.phoneHint')}
          error={fieldError}
          value={phone}
          autoComplete="tel"
          inputMode="tel"
          ltr
          onChange={(event) => setPhone(normalizeIndicDigits(event.target.value))}
        />
        <fieldset className="channel-set">
          <legend className="field-label">{t('auth.channel')}</legend>
          <label>
            <input type="radio" name="channel" value="sms" checked={channel === 'sms'} onChange={() => setChannel('sms')} />
            {t('auth.sms')}
          </label>
          <label>
            <input type="radio" name="channel" value="whatsapp" checked={channel === 'whatsapp'} onChange={() => setChannel('whatsapp')} />
            {t('auth.whatsapp')}
          </label>
        </fieldset>
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        <Button type="submit" block loading={pending} disabled={throttled}>
          {throttled ? t('errors.throttledWait', { seconds: retryIn }) : t('auth.sendCode')}
        </Button>
        <p className="muted legal-hint">
          <Link to={paths.terms}>{t('nav.terms')}</Link>
          {' · '}
          <Link to={paths.privacy}>{t('nav.privacy')}</Link>
        </p>
      </form>
    </div>
  );
}
